import _ from 'lodash';

export function generateResourceStore() {
    return {
        content: {},
        selected: [],
        parent: null,
        notifications: [],
        contentType: null,
        timestamp: Date.now(),
        loading: true,
        status: null,

        path: '',
        query: {},
        hash: null,
        resourceParams: {},
    }
}

export function generateResourceGetters() {
    // getters
    return {
        content: (state) => {
            return state.content;
        },
        selected: (state) => state.selected,
        parent: (state) => state.parent,
        notifications: (state) => state.notifications,
        contentType: (state) => state.contentType,
        timestamp: (state) => state.timestamp,
        loading: (state) => state.loading,
        status: (state) => state.status,
        resourceParams: (state) => state.resourceParams,
        path: (state) => state.path,
        query: (state) => state.query,
        hash: (state) => state.section,
    }
}

export function generateResourceActions(helpers) {
    const { api, uriFormat, getMessage, getErrorMessage } = helpers;

    return {
        getData({ commit, state }, options = {}) {
            commit('processing');

            let path = state.path;
            if (options.path) {
                path = options.path;
                commit('setPath', path);
            }

            return api
                .getData(path, _.assign({}, state.query, options.query))
                .then((response) => {
                    commit('setContent', response);
                    return response;
                })
                .catch((error) => {
                    if (_.get(options, 'setNotification')) {
                        commit('setErrorNotification', getErrorMessage(error, 'get'));
                    } else {
                        commit('setError', getErrorMessage(error, 'get'));
                    }
                })
                .finally((response) => {
                    commit('idle');
                    return response;
                })
        },

        createData({ commit, state }, { data, options = {} }) {
            commit('processing');

            let apiMethod;
            if (_.get(options, 'upload')) {
                apiMethod = api.uploadData(state.path, state.query, data);
            } else {
                apiMethod = api.postData(state.path, state.query, data)
            }
            return apiMethod
                .then((response) => {
                    if (_.get(options, 'addToCollection')) {
                        commit('addToCollection', response);
                    } else {
                        commit('setContent', response);
                    }
                    if (_.get(options, 'setNotification')) {
                        commit('setMessageNotification', getMessage(response, 'create'));
                    }

                    return response;
                })
                .catch((error) => {
                    if (_.get(options, 'setNotification')) {
                        commit('setErrorNotification', getErrorMessage(error, 'create'));
                    } else {
                        commit('setError', getErrorMessage(error, 'create'));
                    }
                })
                .finally((response) => {
                    commit('idle');
                    return response;
                })
        },

        updateBatch({ commit, state }, { data, options = {} }) {
            commit('processing');
            return api
                .putData(state.path, state.query, { data })
                .then((response) => {
                    return api.getData(state.path, state.query)
                })
                .then((response) => {
                    // format response here
                    if (_.get(options, 'setNotification')) {
                        commit('setMessageNotification', getMessage(response, 'updateBatch'));
                    }

                    commit('setContent', response);
                    return response;
                })
                .catch((error) => {
                    if (_.get(options, 'setNotification')) {
                        commit('setErrorNotification', getErrorMessage(error, 'updateBatch'));
                    } else {
                        commit('setError', getErrorMessage(error, 'updateBatch'));
                    }
                })
                .finally(() => commit('idle'))
        },

        updateData({ commit, state }, { data, options = {} }) {
            commit('processing');
            return api
                .patchData(state.path, state.query, data)
                .then((response) => {
                    commit('setContent', response);

                    if (_.get(options, 'setNotification')) {
                        commit('setMessageNotification', getMessage(response, 'update'));
                    }
                    return response;
                })
                .catch((error) => {
                    if (_.get(options, 'setNotification')) {
                        commit('setErrorNotification', getErrorMessage(error, 'update'));
                    } else {
                        commit('setError', getErrorMessage(error, 'update'));
                    }

                    if (options.throwError) {
                        throw error;
                    }
                })
                .finally((response) => {
                    commit('idle');
                    return response;
                })
        },

        deleteData({ commit, state }, options = {}) {
            commit('processing');
            return api.delData(state.path, state.query)
                .then((response) => {
                    if (_.get(options, 'setNotification')) {
                        commit('setMessageNotification', getMessage(response, 'delete'));
                    } else {
                        commit('setMessage', getMessage(response, 'delete'));
                    }

                    return response;
                })
                .catch((error) => {
                    if (_.get(options, 'setNotification')) {
                        commit('setErrorNotification', getErrorMessage(error, 'delete'));
                    } else {
                        commit('setError', getErrorMessage(error, 'delete'));
                    }
                })
                .finally((response) => {
                    commit('idle');
                    return response;
                })
        },

        replaceData({ commit }, data) {
            commit('updateCollection', {
                data: [data],
                meta: { updated: [data.id] },
            })
        },

        setContent({ commit }, content) {
            commit('setContent', content);
        },

        updateCollection({ commit }, data) {
            commit('updateCollection', {
                data,
                meta: {
                    updated: data.map(item => item.id)
                }
            })
        },

        updateParent({ commit, state }, data) {
            const { parent } = state.parent;
            if (!parent) {
                commit('setErrorNotification', 'This content has no parent');
                return;
            }

            return api.patchData(uriFormat(parent.links.self), {}, data)
                .then((response) => {
                    commit('setParent', response);
                    commit('setMessageNotification', getMessage(response, 'update'));
                })
                .catch((error) => {
                    commit('setErrorNotification', getErrorMessage(error, 'update'));
                })
        },

        setSelected({ commit }, selected) {
            commit('setSelected', selected);
        },

        setParent({ commit }, parent) {
            commit('setParent', parent);
        },

        changePath({ commit }, path) {
            commit('setPath', path);
        },

        changeQuery({ commit, state }, query) {
            commit('setQuery', query);
        },

        changeHash({ commit }, hash) {
            commit('setHash', hash);
        },

        setStatus({ commit }, status) {
            console.log(status);
            commit('setStatus', status);
        },
    }
}

export function generateResourceMutations() {
    return {
        updateCollection(state, response) {
            const { created, updated, deleted, attached, detached, forceUpdate = true } = response.meta;

            let data = state.content.data;

            // replacing updated items
            data = _.map(data, item => {
                const replacingItem = _.find(response.data, (itemData => itemData.id == item.id));
                return replacingItem || item;
            });

            // filter out deleted items
            data = _.filter(data, item => {
                return _.indexOf(deleted, item.id) < 0;
            });

            // add items created
            data = _.concat(data, _.filter(response.data, item => _.indexOf(created, item.id) >= 0));

            // add items attached
            data = _.concat(data, _.filter(response.data, item => _.indexOf(attached, item.id) >= 0));

            // filter out detached items
            data = _.filter(data, item => _.indexOf(detached, item.id) < 0);
            state.content.data = data;

            if (forceUpdate) {
                state.timestamp = Date.now();
                state.loading = false;
            } else {

            }
        },

        addToCollection (state, response) {
            let data = _.get(state, 'content.data', []);
            data = _.concat(data, [response]);
            _.set(state, 'content.data', data);
            state.timestamp = Date.now();
        },

        removeFromCollection (state, response) {
            let data = _.get(state, 'content.data', {});
            _.set(state, 'content.data', _.filter(data, item => item.id !== response.id));
            state.timestamp = Date.now();
        },

        setContent (state, response) {
            state.content = response;
            state.parent = _.get(response, 'included.parent');
            state.loading = false;
            state.timestamp = Date.now();
        },

        setParent (state, parent) {
            state.parent = parent;
        },

        setError (state, message) {
            state.content = {
                type: 'error',
                message,
            };
            state.timestamp = Date.now();
        },

        setErrorNotification (state, message) {
            state.notifications.push({
                type: 'error',
                message,
            });
            state.timestamp = Date.now();
        },

        setMessage (state, message) {
            state.content = {
                type: 'message',
                message,
            };
            state.timestamp = Date.now();
        },

        setMessageNotification(state, message) {
            state.notifications.push({
                type: 'message',
                message,
            });
            state.timestamp = Date.now();
        },

        addNotification(state, not) {
            state.notifications.push(not);
        },

        removeNotification(state, index) {
            state.notifications.splice(index, 1);
        },

        setSelected(state, selected) {
            state.selected = selected;
        },

        setPath(state, path) {
            state.path = path;
            const [endpoint, id, relationship, relationshipId] = String(path).split('/');

            state.resourceParams = {
                endpoint, id, relationship, relationshipId,
            };
        },

        setQuery(state, query) {
            state.query = query;
        },

        setHash(state, hash) {
            state.hash = hash;
        },

        setStatus(state, status) {
            state.status = status;
        },

        processing(state) {
            state.loading = true;
        },

        idle(state) {
            state.loading = false;
        },
    }
}



// WEBPACK FOOTER //
// ./seventytwo/72cms-ui/src/store/generators.js